import React from 'react';
import { useTranslation } from 'react-i18next';

import EditIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';

const TabButton = ({ label, active, onClick, isLeft = false }: any) => (
    <button
        type='button'
        onClick={onClick}
        className={`flex-1 h-[50px] min-w-0 text-sm font-semibold border-b-[3px] transition-colors text-center focus:outline-none focus-visible:outline-none focus:ring-0 active:outline-none flex items-center justify-center ${active ? 'text-[#3F8EE8] border-[#3F8EE8]' : 'text-[#D4D4D4] border-transparent'} ${isLeft ? 'shadow-[2px_0px_2px_0px_#0000000A]' : ''}`}
    >
        {label}
    </button>
);

const InfoRow = ({ label, value }: any) => (
    <div className='flex justify-between text-sm py-1'>
        <span className='text-[#757575]'>{label}</span>
        <span className='text-right text-[#2F2F2F] max-w-[55%]'>{value || '--'}</span>
    </div>
);

const InfoColumnItem = ({ label, value }: any) => (
    <div className='flex flex-col text-xs '>
        <span className='text-black font-[400]'>{label}</span>
        <span className='text-[#757575]'>{value || '--'}</span>
    </div>
);

function MobilePendingDrawer({
    tKey,
    row,
    metaData = {} as any,
    threshold = {} as any,
    activeTab,
    onTabChange,
    onEditRow,
}: any) {
    const { t } = useTranslation(undefined, { keyPrefix: tKey });
    const leftInfoItems = [
        { label: t('Edit.numberOfRecords'), value: row.length ?? '-' },
        { label: t('Edit.shipmentNumber'), value: metaData.shipment_number },
        { label: t('Edit.carrier'), value: metaData.carrier_name },
        { label: t('Edit.startLocation'), value: metaData.start_location },
        { label: t('note'), value: metaData.note },
    ];

    const rightInfoItems = [
        { label: t('Edit.addedTime'), value: row.createTime ?? '-' },
        { label: t('Edit.shipper'), value: metaData.shipper_name },
        { label: t('Edit.consignee'), value: metaData.consignee_name },
        { label: t('Edit.endLocation'), value: metaData.end_location },
    ];

    const leftConfigureItems = [
        { label: t('Edit.excursionType'), value: threshold.type },
        {
            label: t('Edit.lowerLimit'),
            value: threshold.lower_limit !== undefined ? `${threshold.lower_limit}°C` : '--',
        },
        {
            label: t('Edit.loggingInterval'),
            value: threshold.interval ? `${Math.round(threshold.interval / 60)}m` : '--',
        },
        {
            label: t('Edit.alarmNotificationEmail'),
            value: row.notifyEmails && row.notifyEmails.length ? row.notifyEmails.join(', ') : '--',
        },
    ];

    const rightConfigureItems = [
        {
            label: t('Edit.upperLimit'),
            value: threshold.upper_limit !== undefined ? `${threshold.upper_limit}°C` : '--',
        },
        { label: t('duration'), value: threshold.time_limit ? `${Math.round(threshold.time_limit / 60)}m` : '--' },
    ];

    const renderInfo = () => (
        <div className='rounded-2xl bg-[#F5F7FB] min-h-[260px] flex flex-col align-center p-2 pl-8'>
            <div className='mt-3 grid grid-cols-2 gap-x-6 gap-y-3 flex-1'>
                <div className='flex flex-col space-y-2 items-start'>
                    {leftInfoItems.map((item: any) => (
                        <InfoColumnItem key={item.label} label={item.label} value={item.value} />
                    ))}
                </div>
                <div className='flex flex-col space-y-2 items-start'>
                    <div className='flex flex-col space-y-2 items-start justify-start w-full'>
                        {rightInfoItems.map((item: any) => (
                            <InfoColumnItem key={item.label} label={item.label} value={item.value} />
                        ))}
                    </div>
                    <div className='flex items-center justify-end w-full pr-10'>
                        <IconButton
                            onClick={() => onEditRow?.(row)}
                            size='small'
                            aria-label='edit row'
                            sx={{
                                backgroundColor: '#3F8EE8',
                                color: '#fff',
                                padding: '6px',
                                '&:hover': { backgroundColor: '#2364b5' },
                            }}
                        >
                            <EditIcon />
                        </IconButton>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderConfigure = () => (
        <div className='rounded-2xl bg-[#F5F7FB] min-h-[260px] flex flex-col p-2 pl-8'>
            <div className='mt-3 grid grid-cols-2 gap-x-6 gap-y-3 flex-1'>
                <div className='flex flex-col space-y-2 items-start'>
                    {leftConfigureItems.map((item: any) => (
                        <InfoColumnItem key={item.label} label={item.label} value={item.value} />
                    ))}
                </div>
                <div className='flex flex-col space-y-2 items-start'>
                    <div className='flex flex-col space-y-2 items-start justify-start w-full'>
                        {rightConfigureItems.map((item: any) => (
                            <InfoColumnItem key={item.label} label={item.label} value={item.value} />
                        ))}
                    </div>
                    <div className='flex items-center justify-end w-full pr-10'>
                        <IconButton
                            onClick={() => onEditRow?.(row)}
                            size='small'
                            aria-label='edit row'
                            sx={{
                                backgroundColor: '#3F8EE8',
                                color: '#fff',
                                padding: '6px',
                                '&:hover': { backgroundColor: '#2364b5' },
                            }}
                        >
                            <EditIcon />
                        </IconButton>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className='flex flex-col gap-4 w-full max-w-full overflow-x-hidden bg-[#F5F7FB]'>
            <div
                className='flex w-full overflow-hidden overflow-x-hidden items-stretch h-[50px]'
                style={{ boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.13), inset 0px 2px 2px rgba(255, 255, 255, 0.61)' }}
            >
                <TabButton
                    label={t('Edit.information')}
                    active={activeTab === 'info'}
                    onClick={() => onTabChange('info')}
                    isLeft
                />
                <TabButton
                    label={t('Edit.configure')}
                    active={activeTab === 'configure'}
                    onClick={() => onTabChange('configure')}
                />
            </div>
            <div>{activeTab === 'info' ? renderInfo() : renderConfigure()}</div>
        </div>
    );
}

export default MobilePendingDrawer;
